import React, { AnchorHTMLAttributes, FC, MouseEvent } from 'react';
import { buildLocalizedPath } from '../../config/languages';
import { useCurrentLocale } from '../../hooks/useCurrentLocale';
import { useLocalizedNavigate } from '../../hooks/useLocalizedNavigate';

type LocalizedLinkProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & {
    /**
     * Absolute application path (WITHOUT language prefix), e.g. '/trps'.
     * The lang prefix is applied automatically based on the current URL.
     */
    to: string;
};

/**
 * Language-aware anchor link.
 * Renders a real <a href="..."> (good for SEO, right-click "open in new tab",
 * etc.) but intercepts left-clicks to do SPA navigation via react-router,
 * preserving the current UI language prefix.
 */
export const LocalizedLink: FC<LocalizedLinkProps> = ({ to, onClick, children, ...rest }) => {
    const lang = useCurrentLocale();
    const navigate = useLocalizedNavigate();
    const localizedHref = buildLocalizedPath(to, lang);

    const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
        onClick?.(event);
        if (event.defaultPrevented) return;
        // Let the browser handle modified clicks (new tab, etc.)
        if (
            event.button !== 0 ||
            event.metaKey ||
            event.ctrlKey ||
            event.shiftKey ||
            event.altKey ||
            rest.target === '_blank'
        ) {
            return;
        }
        event.preventDefault();
        navigate(to);
    };

    return (
        <a href={localizedHref} onClick={handleClick} {...rest}>
            {children}
        </a>
    );
};
